import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import moment from 'moment'
import { useNavigation } from '@react-navigation/native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useSlateToReact } from '@src/screens/NutritionistHub/hooks/nutritionistNotes/useSlateToReact'
import { Icon, Text } from '@src/components/base'
import { CoachAssignment, Note } from '@src/types'
import { CustomEventTypes } from '@src/config'
import { useNutritionistHubTracker } from '@src/screens/NutritionistHub/hooks'
import { DAY_NAME_MONTH_AND_DATE_FORMAT } from '@src/config/momentFormat'
import { CoachAvatar } from '@src/components/Avatar/CoachAvatar'
import { getAbbreviatedCoachName } from '@src/utils'
import { useChatConversation } from '@src/hooks/useChatConversation'

export interface MemberNoteCardProps {
  note: Note
  showLatestTag?: boolean
  hidePinnedAt?: boolean
  coachAssignment?: CoachAssignment
}

export const MemberNoteCard = ({ note, coachAssignment }: MemberNoteCardProps) => {
  const { id, body, occurredAt, creator } = note
  const creatorId = creator.id
  const navigation = useNavigation()
  const styles = useStyleSheet(themedStyles)
  const track = useNutritionistHubTracker()
  const { conciseView } = useSlateToReact(body)

  const chatConversation = useChatConversation(coachAssignment?.coachingKind.key)

  const { coach } = coachAssignment || {}
  const coachProfile = coach?.coachProfile

  const displayNameSource = coach
    ? coach.fullName || `${coach.firstName} ${coach.lastName}`
    : creator.fullName
  const displayName = getAbbreviatedCoachName(displayNameSource)

  const handleNoteCardPress = () => {
    track(CustomEventTypes.NutritionistHubNoteCardTapped, { noteId: id, creatorId })
    navigation.navigate('MemberNoteDetail', {
      noteId: id,
      chatConversationIntercomId: chatConversation?.intercomId,
      coachingKind: coachAssignment?.coachingKind.key,
    })
  }

  return (
    <TouchableOpacity
      accessibilityLabel="note"
      style={styles.noteCard}
      onPress={handleNoteCardPress}
    >
      <CoachAvatar photo={coachProfile?.photo} style={styles.coachAvatar} />
      <View style={styles.content}>
        <View style={styles.noteContent}>{conciseView}</View>
        <View style={styles.footer}>
          <Text type="small" style={styles.metaText} numberOfLines={1}>
            {moment(occurredAt).format(DAY_NAME_MONTH_AND_DATE_FORMAT)}
          </Text>
          <Text type="small" style={styles.metaText} numberOfLines={1}>
            {displayName} - {coachAssignment?.coachingKind.coachTitle}
          </Text>
        </View>
      </View>
      <Icon name="caret-right" size="16" style={styles.caret} />
    </TouchableOpacity>
  )
}

const themedStyles = StyleService.create({
  noteCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'theme.surface.base1',
    backgroundColor: 'theme.background.selected',
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 8,
  },
  caret: {
    color: 'theme.surface.base0',
  },
  content: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    minWidth: 0,
  },
  noteContent: {
    maxHeight: 44,
    overflow: 'hidden',
    width: '100%',
  },
  coachAvatar: {
    height: 40,
    width: 40,
    borderRadius: 20,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  metaText: {
    color: 'theme.text.secondary',
  },
  badge: {
    height: 24,
    backgroundColor: 'theme.info.lightest',
    borderRadius: 32,
    borderWidth: 1,
    borderColor: 'theme.info.lighter',
    paddingHorizontal: 16,
    marginLeft: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    color: 'theme.info.darkest',
  },
})
